import { apiBlobRequest, apiRequest } from './http';
import type { ApiItemResponse, ApiListResponse, CusdecMlDetail } from '../types/api';

export interface CusdecMlListQuery {
  page?: number;
  pageSize?: number;
  referenceDum?: string;
  referenceMessage?: string;
  partnerCode?: string;
  operation?: string;
  validationStatus?: string;
  processingStatus?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CusdecMlListItem {
  id: string;
  messageType: string;
  operation: string;
  rootElement: string;
  filename: string | null;
  partnerCode: string | null;
  referenceDum: string | null;
  referenceMessage: string | null;
  versionDum: number | null;
  dateMlRaw: string | null;
  dateMlTimestamp: string | null;
  validationStatus: string;
  processingStatus: string;
  processingError: string | null;
  correlationId: string;
  inboundDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CusdecMlChildRecords {
  articles: Array<{
    id: string;
    numeroArticle: string | null;
    codeNgp: string | null;
    description: string | null;
    paysOrigine: string | null;
    codePaysOrigine: string | null;
    poidsNet: string | null;
    nombreContenants: number | null;
    typeContenants: string | null;
    containers: Array<{ id: string; idConteneur: string }>;
  }>;
  apurementLines: Array<{
    id: string;
    idConsignataire: string | null;
    numEscale: string | null;
    referenceAndTypeDs: string | null;
    typeDsCode: string | null;
    referenceDs: string | null;
    referenceLot: string | null;
    portChargement: string | null;
    statutCode: string | null;
    statutLabel: string | null;
    goodsLines: Array<{
      id: string;
      numLigne: string | null;
      typeContenant: string | null;
      natureMarchandise: string | null;
      marqueMarchandise: string | null;
      referencesEquipement1: string | null;
      referencesEquipement2: string | null;
    }>;
  }>;
  seals: Array<{ id: string; numScelle: string }>;
  transportMeans: Array<{ id: string; typeMoyenTransport: string | null; matricule: string | null }>;
}

export type CusdecMlRecordDetail = CusdecMlDetail & { children: CusdecMlChildRecords; filename: string | null };

function queryString(query: CusdecMlListQuery): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export async function listCusdecMlMessages(query: CusdecMlListQuery): Promise<ApiListResponse<CusdecMlListItem>> {
  return apiRequest<ApiListResponse<CusdecMlListItem>>(`/maritime/cusdec-ml${queryString(query)}`);
}

export async function getCusdecMlMessage(id: string): Promise<CusdecMlRecordDetail> {
  const response = await apiRequest<ApiItemResponse<CusdecMlRecordDetail>>(`/maritime/cusdec-ml/${id}`);
  return response.data;
}

export async function getCusdecMlRawXml(id: string): Promise<string> {
  const { blob } = await apiBlobRequest(`/maritime/cusdec-ml/${id}/raw`);
  return blob.text();
}

export async function reprocessCusdecMlMessage(id: string): Promise<{ id: string; processingStatus: string }> {
  const response = await apiRequest<ApiItemResponse<{ id: string; processingStatus: string }>>(
    `/maritime/cusdec-ml/${id}/reprocess`,
    { method: 'POST' },
  );
  return response.data;
}

export async function replayCusdecMlMessage(id: string): Promise<{ id: string; processingStatus: string }> {
  const response = await apiRequest<ApiItemResponse<{ id: string; processingStatus: string }>>(
    `/maritime/cusdec-ml/${id}/replay`,
    { method: 'POST' },
  );
  return response.data;
}
